import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Button } from 'components/Button/Button'
import { ViewPollParticipantsModal } from 'components-ref/organisms/modals/view-poll-participants/view-poll-participants'
import { type ISelectedAnswerPoll } from 'types/interfaces/ISelectedAnswerPoll'
import { type IPollResponse } from 'types/interfaces/ITypes'

const calculatePercentage = (total: number, count: number) => {
    if (count === 0 && total === 0) return 0
    return Math.round((100 * count) / total)
}

interface PollProps {
    pollId: string
    title: string
    options: PollOption[]
    answer: string | number | null
    handleSelect?: (_answer: string) => void
    isMyPost: boolean
}

interface PollOption {
    id: string
    text: string
    count: number
}

const Poll = ({
    pollId,
    title,
    options,
    answer,
    handleSelect,
    isMyPost
}: PollProps) => {
    const [isParticipantsModalOpened, setIsParticipantsModalOpened] =
        useState(false)
    const [answerSelected, setAnswerSelected] = useState<ISelectedAnswerPoll>({
        answerId:'',
        key: '',
        text: '',
        count: 0,
        total: 0
    })
    const { t } = useTranslation()
    const keys = [t('a'), t('b'), t('c'), t('d')]
    const total = options.reduce((prev, curr) => prev + curr.count, 0)

    const handleParticipantsModalClose = () => {
        setIsParticipantsModalOpened(false)
    }

    const handleSelectOption = (
        e: React.MouseEvent<HTMLDivElement, MouseEvent>,
        option: PollOption,
        idx: number
    ) => {
        if (handleSelect) {
            e.stopPropagation()
            if (!isMyPost) {
                !answer && !isMyPost && handleSelect(option.id)
            } else {
                setAnswerSelected({
                    answerId: option.id,
                    key: keys[idx],
                    count: option.count,
                    total,
                    text: option.text
                })
                setIsParticipantsModalOpened(true)
            }
        }
    }

    return (
        <>
            {isParticipantsModalOpened && (
                <ViewPollParticipantsModal
                    pollId={pollId}
                    onClose={handleParticipantsModalClose}
                    answerSelected={answerSelected}
                />
            )}
            <div
                className='poll'
                onClick={e => {
                    e.stopPropagation()
                }}
            >
                <div className='poll__header'>
                    <p>{title}</p>
                </div>
                <div className='poll__content'>
                    {options?.map((option, idx) => {
                        return (
                            <div
                                key={idx}
                                className={'poll__option'}
                                onClick={e => {
                                    handleSelectOption(e, option, idx)
                                }}
                            >
                                {(answer ?? isMyPost) && (
                                    <div
                                        style={{
                                            width: `${calculatePercentage(
                                                total,
                                                option.count
                                            )}%`
                                        }}
                                        className='poll__option__percentage'
                                    ></div>
                                )}

                                <div className='poll__option__icon'>
                                    {keys[idx]}
                                </div>
                                <div className='poll__option__input'>
                                    {option.text}
                                </div>
                                {(answer ?? isMyPost) && (
                                    <div className='poll__option__count'>
                                        {option.count}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            </div>
        </>
    )
}

interface AskQuestionProps {
    title: string
    answer: string | null
    responseCount: number
    handleSelect?: (_answer: string) => void
    isMyPost: boolean
}
const AskQuestion = ({
    title,
    answer,
    responseCount,
    handleSelect,
    isMyPost
}: AskQuestionProps) => {
    const [inputText, setInputText] = useState('')
    const { t } = useTranslation()
    const navigate = useNavigate()
    useEffect(() => {
        if (answer) {
            if (isMyPost) {
                setInputText('')
            } else {
                setInputText(answer)
            }
        }
    }, [answer, isMyPost])

if (isMyPost) {
    return (
        <div
            className='askQuestion'
            onClick={e => {
                e.stopPropagation()
            }}
        >
            <div className='askQuestion__header'>
                <p>{title}</p>
            </div>

            <div className='askQuestion__content'>
                <div
                    className='askQuestion__option askQuestion__option--responses'
                    onClick={e => {
                        e.stopPropagation()
                        navigate('/chat')
                    }}
                >
            <div className='askQuestion__option__responses'>
                {responseCount === 0 ? (
                    <>
                        No <span className='askQuestion__option__responses__label'>Responses</span>
                    </>
                ) : responseCount === 1 ? (
                    <>
                        1 <span className='askQuestion__option__responses__label'>Response</span>
                    </>
                ) : (
                    <>
                        {responseCount}{' '}
                        <span className='askQuestion__option__responses__label'>Responses</span>
                    </>
                )}
            </div>
                </div>
            </div>
        </div>
    )
}

    return (
        <div
            className='askQuestion'
            onClick={e => {
                e.stopPropagation()
            }}
        >
            <div className='askQuestion__header'>
                <p>{title}</p>
            </div>
            <div className='askQuestion__content'>
                <div
                    className={`askQuestion__option ${
                        answer ? 'askQuestion__option--selected' : ''
                    }`}
                >
                    <div className='askQuestion__option__input'>
                        <input
                            type='text'
                            name='questionText'
                            placeholder={t('typeSomething')}
                            maxLength={50}
                            value={inputText}
                            onChange={e => {
                                setInputText(e.target.value)
                            }}
                            disabled={!!answer || isMyPost}
                        />
                    </div>
                </div>
            </div>
            {isMyPost ? null : !answer && inputText ? (
                <div className='askQuestion__footer'>
                    <Button
                        text={t('cancel')}
                        color='white'
                        font='mont-14-normal'
                        width='12'
                        height='3'
                        clickFn={() => {
                            setInputText('')
                        }}
                    />
                    <Button
                        text={t('save')}
                        color='black'
                        font='mont-14-normal'
                        width='13'
                        height='3'
                        clickFn={e => {
                            if (handleSelect) {
                                e?.stopPropagation()
                                inputText && handleSelect(inputText)
                            }
                        }}
                    />
                </div>
            ) : null}
        </div>
    )
}

interface YesNoQuestionProps {
    pollId: string
    title: string
    options: Array<{
        id: string
        text: string
        count: number
    }>
    answer: string | number | null
    handleSelect?: (_answer: string) => void
    isMyPost: boolean
}
const YesNoQuestion = ({
    pollId,
    title,
    options,
    answer,
    handleSelect,
    isMyPost
}: YesNoQuestionProps) => {
    const [isParticipantsModalOpened, setIsParticipantsModalOpened] =
        useState(false)
    const [answerSelected, setAnswerSelected] = useState<ISelectedAnswerPoll>({
        answerId: '',
        key: '',
        text: '',
        count: 0,
        total: 0
    })
    const { t } = useTranslation()
    const keys = [t('a'), t('b'), t('c'), t('d')]
    const total = options.reduce((prev, curr) => prev + curr.count, 0)
const higherRated = options.length
    ? options.reduce((prev, current) =>
        prev.count > current.count ? prev : current
      )
    : null

    const handleParticipantsModalClose = () => {
        setIsParticipantsModalOpened(false)
    }

    const handleSelectOption = (
        e: React.MouseEvent<HTMLDivElement, MouseEvent>,
        option: PollOption,
        idx: number
    ) => {
        if (handleSelect) {
            e.stopPropagation()
            if (!isMyPost) {
                !answer && !isMyPost && handleSelect(option.id)
            } else {
                setAnswerSelected({
                    answerId: option.id,
                    key: keys[idx],
                    count: option.count,
                    total,
                    text: option.text.toUpperCase()
                })
                setIsParticipantsModalOpened(true)
            }
        }
    }

    return (
        <>
            {isParticipantsModalOpened && (
                <ViewPollParticipantsModal
                    pollId={pollId}
                    onClose={handleParticipantsModalClose}
                    answerSelected={answerSelected}
                />
            )}
            <div
                className='yesNoQuestion'
                onClick={e => {
                    e.stopPropagation()
                }}
            >
                <div className='yesNoQuestion__input'>{title}</div>
                <div className='yesNoQuestion__buttons'>
                    {options.map((option, idx) => {
                        return (
                            <div
                                key={idx}
                                className={`yesNoQuestion__button yesNoQuestion__button--${
                                    option.text
                                } ${
                                    (answer ?? isMyPost) &&
                                    option.id === higherRated?.id
                                        ? 'yesNoQuestion__button--percentage'
                                        : ''
                                }`}
                                onClick={e => {
                                    handleSelectOption(e, option, idx)
                                }}
                            >
                                <div className='yesNoQuestion__button__text'>
                                    {option.text}
                                    {(answer ?? isMyPost) && (
                                        <span className='yesNoQuestion__button__text--count'>
                                            {calculatePercentage(
                                                total,
                                                option.count
                                            )}
                                            %
                                        </span>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </>
    )
}
export interface PollAnswer {
    pollId: string
    answer?: string | number | null
    answerId?: string
}
interface PostPollProps {
    pollData: IPollResponse | null
    setAnswer: (values: PollAnswer) => void
    isMyPost?: boolean
}

export const PostPoll = ({
    pollData,
    setAnswer,
    isMyPost = false
}: PostPollProps) => {
    return (
        <>
            {pollData?.type === 'poll' && (
                <Poll
                    pollId={pollData.id}
                    title={pollData.question}
                    options={pollData.answers_count}
                    answer={pollData.is_answered}
                    handleSelect={value => {
                        setAnswer({ pollId: pollData.id, answerId: value })
                    }}
                    isMyPost={isMyPost}
                />
            )}
            {pollData?.type === 'ask' && (
                <AskQuestion
                    title={pollData.question}
                    answer={
                        typeof pollData.answer === 'string'
                            ? pollData.answer
                            : ''
                    }
                    responseCount={pollData.response_count ?? 0}
                    handleSelect={value => {
                        setAnswer({
                            pollId: pollData.id,
                            answer: value
                        })
                    }}
                    isMyPost={isMyPost}
                />
            )}
            {pollData?.type === 'yes/no' && (
                <YesNoQuestion
                    pollId={pollData.id}
                    title={pollData.question}
                    options={pollData.answers_count}
                    answer={pollData.is_answered}
                    handleSelect={value => {
                        setAnswer({ pollId: pollData.id, answerId: value })
                    }}
                    isMyPost={isMyPost}
                />
            )}
        </>
    )
}
